import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Plus, Edit, Trash2, Sliders, SearchX } from 'lucide-react';
import toast from 'react-hot-toast';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import IconButton from '../components/common/IconButton';
import SmartSearchBar from '../components/leads/SmartSearchBar';
import SmartValueInput, { InferredInputType, inferInputType } from '../components/parameters/SmartValueInput';
import { categorize, Category } from '../components/parameters/parameterCategories';
import {
  Parameter,
  parametersApi
} from '../services/parametersService';
import { handleApiError } from '../services/settingsService';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageSubtitle = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;

  @media (max-width: 767px) {
    max-width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover:not(:disabled) {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover:not(:disabled) { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover:not(:disabled) { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const KeyCell = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 600;
  direction: ltr;
  display: inline-block;
`;

const ValueCell = styled.span`
  font-size: 13px;
  color: var(--text-primary);
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  direction: ltr;
`;

const SecondaryText = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

const DescriptionCell = styled.span`
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 600;
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  direction: ltr;
  text-align: right;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }

  &:disabled {
    background: var(--background-secondary);
    color: var(--text-secondary);
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea<{ hasError?: boolean }>`
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  min-height: 70px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const HelperText = styled.div`
  font-size: 12px;
  color: var(--text-tertiary);
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
`;

const CategorySection = styled.section`
  margin-bottom: 28px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 0 4px;
`;

const SectionIconBubble = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${props => props.$color}1f;
  color: ${props => props.$color};
  flex-shrink: 0;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--text-primary);
`;

const SectionCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--background-secondary, #f5f6f8);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  background: var(--surface-color, #ffffff);
  border: 1px dashed var(--border-color, #e6e9ef);
  border-radius: var(--border-radius-lg, 12px);
  text-align: center;
  gap: 16px;
`;

const EmptyIconBubble = styled.div`
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--background-secondary, #f5f6f8);
  color: var(--text-tertiary, #a1a4b2);
`;

const EmptyTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--text-primary);
`;

const EmptyText = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  max-width: 420px;
  line-height: 1.5;
`;

const LoadingState = styled.div`
  padding: 64px 24px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 15px;
`;

const CategoryHint = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${props => props.$color}14;
  color: ${props => props.$color};
  font-size: 12px;
  font-weight: 500;
  align-self: flex-start;

  strong {
    font-weight: 700;
  }
`;

interface FormData {
  parameterKey: string;
  parameterValue: string;
  description: string;
}

interface FormErrors {
  parameterKey?: string;
  parameterValue?: string;
  description?: string;
}

const ParametersManagementPage: React.FC = () => {
  const [parameters, setParameters] = useState<Parameter[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingParameter, setEditingParameter] = useState<Parameter | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    parameterKey: '',
    parameterValue: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  // סוג השדה נקבע פעם אחת בעת פתיחת המודאל ולא משתנה בזמן הקלדה (UX יציב)
  const [inputType, setInputType] = useState<InferredInputType>('text');

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    parameter: Parameter | null;
    loading: boolean;
  }>({ isOpen: false, parameter: null, loading: false });

  useEffect(() => {
    loadParameters();
  }, []);

  const loadParameters = async () => {
    try {
      setLoading(true);
      const data = await parametersApi.getAll();
      setParameters(data);
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const filteredParameters = useMemo(() => {
    if (!searchTerm) return parameters;
    const search = searchTerm.toLowerCase();
    return parameters.filter(p =>
      p.parameterKey.toLowerCase().includes(search) ||
      p.parameterValue?.toLowerCase().includes(search) ||
      p.description?.toLowerCase().includes(search)
    );
  }, [parameters, searchTerm]);

  const groupedParameters = useMemo(() => {
    const groups = new Map<string, { category: Category; items: Parameter[] }>();
    for (const p of filteredParameters) {
      const cat = categorize(p.parameterKey);
      if (!groups.has(cat.id)) {
        groups.set(cat.id, { category: cat, items: [] });
      }
      groups.get(cat.id)!.items.push(p);
    }
    return Array.from(groups.values()).sort(
      (a, b) => a.category.order - b.category.order
    );
  }, [filteredParameters]);

  const previewCategory = useMemo(() => {
    const key = formData.parameterKey.trim();
    return key ? categorize(key) : null;
  }, [formData.parameterKey]);

  const openModal = (parameter?: Parameter) => {
    if (parameter) {
      setEditingParameter(parameter);
      const value = parameter.parameterValue ?? '';
      setFormData({
        parameterKey: parameter.parameterKey,
        parameterValue: value,
        description: parameter.description ?? ''
      });
      setInputType(inferInputType(value));
    } else {
      setEditingParameter(null);
      setFormData({
        parameterKey: '',
        parameterValue: '',
        description: ''
      });
      setInputType('text');
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingParameter(null);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!editingParameter && !formData.parameterKey.trim()) {
      errors.parameterKey = 'מפתח הפרמטר הוא שדה חובה';
    } else if (!editingParameter && !/^[a-z][a-z0-9_]*$/i.test(formData.parameterKey.trim())) {
      errors.parameterKey = 'מפתח חייב לכלול אותיות באנגלית, ספרות וקו תחתון בלבד';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const trimmedValue = formData.parameterValue;
      const trimmedDescription = formData.description.trim() || null;

      if (editingParameter) {
        await parametersApi.update(editingParameter.id, {
          parameterValue: trimmedValue,
          description: trimmedDescription
        });
        toast.success('הפרמטר עודכן בהצלחה');
      } else {
        await parametersApi.create({
          parameterKey: formData.parameterKey.trim(),
          parameterValue: trimmedValue,
          description: trimmedDescription
        });
        toast.success('הפרמטר נוצר בהצלחה');
      }

      closeModal();
      await loadParameters();
    } catch (error) {
      const errorMessage = handleApiError(error);
      // ניסיון להציג שגיאה ברמת השדה כאשר מדובר במפתח כפול
      if (errorMessage.includes('מפתח') && !editingParameter) {
        setFormErrors({ parameterKey: errorMessage });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (parameter: Parameter) => {
    setConfirmDialog({ isOpen: true, parameter, loading: false });
  };

  const confirmDelete = async () => {
    if (!confirmDialog.parameter) return;

    try {
      setConfirmDialog(prev => ({ ...prev, loading: true }));
      await parametersApi.delete(confirmDialog.parameter.id);
      toast.success('הפרמטר נמחק בהצלחה');
      setConfirmDialog({ isOpen: false, parameter: null, loading: false });
      await loadParameters();
    } catch (error) {
      toast.error(handleApiError(error));
      setConfirmDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const columns = [
    {
      key: 'parameterKey',
      title: 'מפתח',
      width: '300px',
      render: (value: string) => <KeyCell>{value}</KeyCell>
    },
    {
      key: 'parameterValue',
      title: 'ערך',
      width: '350px',
      render: (value: string | null) => (
        <ValueCell title={value ?? ''}>{value ?? <SecondaryText>-</SecondaryText>}</ValueCell>
      )
    },
    {
      key: 'description',
      title: 'תיאור',
      width: '350px',
      render: (value: string | null) => (
        value ? <DescriptionCell>{value}</DescriptionCell> : <SecondaryText>-</SecondaryText>
      )
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '100px',
      render: (_: any, record: Parameter) => (
        <ActionsCell>
          <IconButton
            icon={Edit}
            onClick={() => openModal(record)}
            title="עריכה"
          />
          <IconButton
            icon={Trash2}
            onClick={() => handleDelete(record)}
            title="מחיקה"
            variant="danger"
          />
        </ActionsCell>
      )
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <TitleBlock>
          <PageTitle>
            <Sliders size={28} />
            ניהול פרמטרים
          </PageTitle>
          <PageSubtitle>הגדרה וניהול פרמטרי המערכת של החברה</PageSubtitle>
        </TitleBlock>
        <Button variant="primary" onClick={() => openModal()}>
          <Plus size={16} />
          פרמטר חדש
        </Button>
      </PageHeader>

      <Toolbar>
        <SearchWrapper>
          <SmartSearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="חיפוש לפי מפתח, ערך או תיאור..."
          />
        </SearchWrapper>
      </Toolbar>

      {loading ? (
        <LoadingState>טוען פרמטרים...</LoadingState>
      ) : parameters.length === 0 ? (
        <EmptyState>
          <EmptyIconBubble>
            <Sliders size={36} />
          </EmptyIconBubble>
          <EmptyTitle>עדיין אין פרמטרים במערכת</EmptyTitle>
          <EmptyText>
            פרמטרים מאפשרים לך לשלוט בהתנהגות המערכת ובמיתוג שלה ללא צורך בשינוי קוד.
            צור את הפרמטר הראשון כדי להתחיל.
          </EmptyText>
          <Button variant="primary" onClick={() => openModal()}>
            <Plus size={16} />
            פרמטר חדש
          </Button>
        </EmptyState>
      ) : groupedParameters.length === 0 ? (
        <EmptyState>
          <EmptyIconBubble>
            <SearchX size={36} />
          </EmptyIconBubble>
          <EmptyTitle>לא נמצאו פרמטרים תואמים</EmptyTitle>
          <EmptyText>
            לא הצלחנו למצוא פרמטרים שתואמים את החיפוש "{searchTerm}".
            נסה מילת חיפוש אחרת או נקה את החיפוש.
          </EmptyText>
          <Button onClick={() => setSearchTerm('')}>נקה חיפוש</Button>
        </EmptyState>
      ) : (
        groupedParameters.map(({ category, items }) => {
          const Icon = category.icon;
          return (
            <CategorySection key={category.id}>
              <SectionHeader>
                <SectionIconBubble $color={category.color}>
                  <Icon size={18} />
                </SectionIconBubble>
                <SectionTitle>{category.label}</SectionTitle>
                <SectionCount>{items.length}</SectionCount>
              </SectionHeader>
              <DataTable data={items} columns={columns} loading={false} />
            </CategorySection>
          );
        })
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingParameter ? 'עריכת פרמטר' : 'הוספת פרמטר חדש'}
        width="560px"
        footer={
          <>
            <Button onClick={closeModal} disabled={isSubmitting}>
              ביטול
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'שומר...' : 'שמירה'}
            </Button>
          </>
        }
      >
        <FormContainer>
          <FormGroup>
            <Label>מפתח הפרמטר *</Label>
            <Input
              type="text"
              value={formData.parameterKey}
              onChange={(e) => setFormData(prev => ({ ...prev, parameterKey: e.target.value }))}
              hasError={!!formErrors.parameterKey}
              placeholder="parameter_key"
              disabled={!!editingParameter}
            />
            {!!editingParameter && (
              <HelperText>לא ניתן לשנות מפתח לאחר יצירת הפרמטר</HelperText>
            )}
            {!editingParameter && previewCategory && !formErrors.parameterKey && (
              <CategoryHint $color={previewCategory.color}>
                <previewCategory.icon size={14} />
                <span>ישויך לקטגוריה: <strong>{previewCategory.label}</strong></span>
              </CategoryHint>
            )}
            {formErrors.parameterKey && <ErrorMessage>{formErrors.parameterKey}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>ערך</Label>
            <SmartValueInput
              inputType={inputType}
              value={formData.parameterValue}
              onChange={(next) => setFormData(prev => ({ ...prev, parameterValue: next }))}
              hasError={!!formErrors.parameterValue}
            />
            {formErrors.parameterValue && <ErrorMessage>{formErrors.parameterValue}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>תיאור</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              hasError={!!formErrors.description}
              placeholder="תיאור הפרמטר ושימושו במערכת"
            />
            {formErrors.description && <ErrorMessage>{formErrors.description}</ErrorMessage>}
          </FormGroup>
        </FormContainer>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, parameter: null, loading: false })}
        onConfirm={confirmDelete}
        title="מחיקת פרמטר"
        message={`האם למחוק את הפרמטר '${confirmDialog.parameter?.parameterKey}'? פעולה זו אינה הפיכה.`}
        type="danger"
        loading={confirmDialog.loading}
      />
    </PageContainer>
  );
};

export default ParametersManagementPage;
